<template>
  <section class="relative min-h-screen flex items-center bg-white dark:bg-gray-950 pt-16 sm:pt-20 transition-colors duration-500">
    <!-- Content -->
    <div class="relative z-10 px-6 sm:px-8 max-w-5xl mx-auto w-full py-20 sm:py-28">
      <div class="max-w-2xl">
        <!-- Identity -->
        <p class="mb-10 sm:mb-14 text-xs font-mono uppercase tracking-[0.2em] text-gray-500 dark:text-gray-500 animate-fade-in-up">
          Paulo Vitor <span class="text-gray-300 dark:text-gray-700">/</span> {{ t('roleTitle') }}
        </p>

        <!-- Main heading -->
        <h1 class="animate-fade-in-up" style="animation-delay: 0.1s">
          <span class="block text-5xl sm:text-7xl font-semibold tracking-tighter text-gray-900 dark:text-white leading-[1.05]">{{ t('headline') }}</span>
        </h1>

        <!-- Hairline -->
        <div class="mt-8 mb-8 h-px w-16 bg-gray-300 dark:bg-gray-700 animate-fade-in-up" style="animation-delay: 0.15s"></div>

        <!-- Subtitle -->
        <p class="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-xl leading-relaxed animate-fade-in-up" style="animation-delay: 0.2s">
          {{ t('tagline') }}
        </p>

        <!-- Tech stack -->
        <p class="mt-10 text-sm text-gray-400 dark:text-gray-500 tracking-wide animate-fade-in-up" style="animation-delay: 0.3s">
          {{ techStack.join('  ·  ') }}
        </p>

        <!-- CTA -->
        <div class="mt-12 flex items-center gap-8 animate-fade-in-up" style="animation-delay: 0.4s">
          <a href="#contact" class="group inline-flex items-center gap-3 text-sm font-semibold text-gray-900 dark:text-white">
            <span class="border-b-2 border-gray-900 dark:border-white pb-1 group-hover:border-amber-600 dark:group-hover:border-amber-500 group-hover:text-amber-600 dark:group-hover:text-amber-500 transition-colors">{{ t('getInTouch') }}</span>
            <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </a>
          <a href="#projects" class="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            {{ t('seeMyWork') }}
          </a>
        </div>

        <!-- Social links -->
        <div class="flex items-center gap-5 mt-14 pt-8 border-t border-gray-200 dark:border-gray-800 animate-fade-in-up" style="animation-delay: 0.5s">
          <a href="https://github.com/pvpmartins" target="_blank" aria-label="GitHub" class="text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
          </a>
          <a href="https://linkedin.com/in/pvpmartins" target="_blank" aria-label="LinkedIn" class="text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <span class="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-gray-400 dark:text-gray-600">
            <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
            {{ t('available') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="absolute bottom-10 left-1/2 -translate-x-1/2 animate-fade-in-up" style="animation-delay: 0.8s">
      <a href="#about" aria-label="Scroll to about" class="block w-px h-12 bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-700 to-gray-400 dark:to-gray-500"></a>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from '@/i18n'

const { t } = useI18n()

const techStack = ['Vue.js', 'React', 'TypeScript', 'Python', 'Ruby on Rails', 'AI/ML']
</script>
